import React, { useCallback, useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormHelperText,
  Grid,
  List,
  ListItem,
  TextField,
  Typography,
} from '@material-ui/core';
import moment from 'moment';
import { compose } from 'redux';
import { translate as translateHOC } from 'react-admin';
import classnames from 'classnames';
import AddNoteIcon from '../../../icons/AddNoteIcon';
import RemoveNoteIcon from '../../../icons/RemoveNoteIcon';
import DownloadIcon from '../../../icons/DownloadIcon';
import { MainColors } from '../../../shared/colors';

const styles = ({ spacing: { unit } }) => ({
  icons: {
    width: 20,
    height: 20,
    marginLeft: 8,
  },
  iconsDisabled: {
    width: 20,
    height: 20,
    marginLeft: 8,
    fill: 'grey',
  },
  refundItem: {
    flexDirection: 'column',
    padding: `${unit}px 0`,
    gap: `${unit * 2}px`,
  },
  refundItemTopSection: {
    gap: `${unit * 2}px`,
  },
  refundItemField: {
    flexGrow: 1,
  },
  refundItemButtons: {
    gap: `${unit}px`,
    width: 'fit-content',
    alignItems: 'center',
  },
  refundItemButton: {
    height: 'fit-content',
  },
  divider: {
    marginTop: unit,
  },
  receiptItem: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: `${unit}px 0`,
    gap: `${unit * 2}px`,
  },
  receiptItemContent: {
    width: 'fit-content',
  },
  receiptItemButton: {
    textTransform: 'none',
  },
  receiptItemHelper: {
    paddingLeft: unit * 2,
  },
  lists: {
    width: '100%',
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  chip: {
    borderRadius: 4,
    color: MainColors.purple,
    backgroundColor: '#f3e8f7',
    height: unit * 3,
    '& > span': {
      padding: `0 ${unit}px 2px ${unit}px`,
    },
  },
});

const RefundItem = withStyles(styles)(({ classes, receipt, onRefund, isLast, translate, isDisabled }) => {
  const [amount, setAmount] = useState(receipt.amount);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [note, setNote] = useState('');

  const toggleNote = useCallback(() => {
    setIsAddingNote(prev => {
      if (!prev) {
        setNote('');
      }
      return !prev;
    });
  }, []);

  const getIsAmountInvalid = useCallback(() => {
    if (Number(amount) <= 0) return true;
    if (Number(amount) > receipt.amount) return true;
    return false;
  }, [amount, receipt.amount]);

  const getIsSubmitBtnDisabled = useCallback(() => {
    if (amount === '') return true;
    if (getIsAmountInvalid()) return true;
    if (isAddingNote && note === '') return true;
    return false;
  }, [amount, note, isAddingNote, getIsAmountInvalid]);

  const onSubmit = useCallback(() => {
    onRefund({
      paymentMethodPspReference: receipt.paymentMethodPspReference,
      amount,
      ...(note && { note }),
    });
  }, [amount, note, onRefund, receipt.paymentMethodPspReference]);

  return (
    <>
      <ListItem className={classes.refundItem}>
        <Grid container className={classes.refundItemTopSection}>
          <TextField
            label={translate('resources.invoice.refundDialog.amount')}
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            error={getIsAmountInvalid()}
            helperText={
              getIsAmountInvalid()
                ? translate('resources.invoice.refundDialog.refundBetween', { maxAmount: receipt.amount })
                : ''
            }
            className={classes.refundItemField}
            InputProps={{
              endAdornment: <FormHelperText>{receipt.currency}</FormHelperText>,
            }}
            disabled={isDisabled}
          />
          <Grid container item className={classes.refundItemButtons}>
            <Button
              onClick={toggleNote}
              variant="outlined"
              color={isDisabled ? 'default' : 'primary'}
              className={classes.refundItemButton}
              disabled={isDisabled}
            >
              {translate('resources.invoice.setAsPaidDialog.notesLabel')}
              {isAddingNote ? (
                <RemoveNoteIcon
                  className={classnames({ [classes.icons]: !isDisabled, [classes.iconsDisabled]: isDisabled })}
                />
              ) : (
                <AddNoteIcon
                  className={classnames({ [classes.icons]: !isDisabled, [classes.iconsDisabled]: isDisabled })}
                />
              )}
            </Button>
            <Button
              onClick={onSubmit}
              variant="contained"
              color="primary"
              disabled={getIsSubmitBtnDisabled() || isDisabled}
              className={classes.refundItemButton}
            >
              {translate('resources.invoice.refund')}
            </Button>
          </Grid>
        </Grid>
        {isAddingNote && (
          <TextField
            multiline
            fullWidth
            label={translate('resources.invoice.setAsPaidDialog.notesLabel')}
            value={note}
            onChange={e => setNote(e.target.value)}
          />
        )}
        <Grid container item direction="column" className={classes.refundItemContent}>
          <FormHelperText>{`${translate('resources.invoice.fields.maxRefundAmount', { amount: receipt.amount })} ${
            receipt.currency
          } - ${moment(receipt.date).format('L LTS')} - ${receipt.paymentMethodPspReference}`}</FormHelperText>
        </Grid>
      </ListItem>
      {!isLast && <Divider className={classes.divider} />}
    </>
  );
});

const ReceiptItem = withStyles(styles)(({ classes, receipt, isLast, onDownload, translate }) => {
  return (
    <>
      <ListItem className={classes.receiptItem}>
        <Grid container direction="column" className={classes.receiptItemContent}>
          <Button variant="text" color="primary" className={classes.refundItemButton} onClick={onDownload}>
            {`${receipt.amount} ${receipt.currency} ${translate('resources.invoice.status.REFUNDED')} - ${moment(
              receipt.date
            ).format('L LTS')}`}
            <DownloadIcon className={classes.icons} />
          </Button>
          <FormHelperText className={classes.receiptItemHelper}>{`${translate('resources.invoice.fields.pspRef')}: ${
            receipt.paymentMethodPspReference
          }`}</FormHelperText>
        </Grid>
      </ListItem>
      {!isLast && <Divider className={classes.divider} />}
    </>
  );
});

const Refund = ({
  classes,
  open,
  onClose,
  refund,
  refundableReceipts,
  refundedReceipts,
  downloadRefundNote,
  translate,
  inProgress,
}) => {
  const onRefund = payload => refund(payload);
  const onDownload = paymentRefundPspReference => downloadRefundNote(paymentRefundPspReference);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" disableBackdropClick>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography variant="title">{translate('resources.invoice.refund')}</Typography>
        {inProgress && (
          <Chip className={classes.chip} label={translate('resources.invoice.status.IN_PROGRESS').toLowerCase()} />
        )}
      </DialogTitle>
      <DialogContent>
        <Grid container>
          <Typography variant="subheading">{translate('resources.invoice.refundDialog.refundableReceipts')}</Typography>
          <List className={classes.lists}>
            {refundableReceipts.length === 0 && (
              <ListItem>
                <Typography variant="body2">
                  {translate('resources.invoice.refundDialog.noRefundableReceipts')}
                </Typography>
              </ListItem>
            )}
            {refundableReceipts.map(receipt => (
              <RefundItem
                key={receipt.id}
                receipt={receipt}
                onRefund={onRefund}
                isLast={refundableReceipts.indexOf(receipt) === refundableReceipts.length - 1}
                translate={translate}
                isDisabled={inProgress}
              />
            ))}
          </List>
        </Grid>
        <Grid container>
          <Typography variant="subheading">{translate('resources.invoice.refundDialog.refundedReceipts')}</Typography>
          <List className={classes.lists}>
            {refundedReceipts.length === 0 && (
              <ListItem>
                <Typography variant="body2">
                  {translate('resources.invoice.refundDialog.noRefundedReceipts')}
                </Typography>
              </ListItem>
            )}
            {refundedReceipts.map(receipt => (
              <ReceiptItem
                key={receipt.id}
                receipt={receipt}
                isLast={refundedReceipts.indexOf(receipt) === refundedReceipts.length - 1}
                onDownload={() => onDownload(receipt.pspReference)}
                translate={translate}
              />
            ))}
          </List>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="outlined">
          {translate('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

Refund.propTypes = {
  classes: PropTypes.shape({}),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  refund: PropTypes.func,
  refundableReceipts: PropTypes.arrayOf(PropTypes.shape({})),
  refundedReceipts: PropTypes.arrayOf(PropTypes.shape({})),
  downloadRefundNote: PropTypes.func,
  translate: PropTypes.func,
  inProgress: PropTypes.bool,
};

export default compose(
  withStyles(styles),
  translateHOC
)(Refund);
